import React from 'react';
import {
  Box,
  ButtonBase,
  Collapse,
  List,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTheme } from '@mui/material/styles';
import NavItem from './NavItem';
import { isNavItemActive } from './isActiveRoute';

const NavSection = ({
  sectionId,
  title,
  items,
  pathname,
  onItemActivate,
  expanded,
  onToggle,
  /** When false, render links only (no collapsible header). For top-level Dashboard / Profile rows. */
  collapsible = true,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const headingId = `nav-section-${sectionId}-label`;
  const panelId = `nav-section-${sectionId}-panel`;

  const sectionTitleColor = isDark ? 'rgba(255, 255, 255, 0.45)' : 'text.secondary';
  const sectionHoverBg = isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(15, 23, 42, 0.04)';

  const list = (
    <List disablePadding dense sx={{ py: collapsible ? 0.5 : 0, pl: collapsible ? 0.25 : 0 }}>
      {items.map((item) => (
        <NavItem
          key={item.id}
          label={item.label}
          icon={item.icon}
          selected={item.action ? false : isNavItemActive(item, pathname)}
          onClick={() => onItemActivate(item)}
        />
      ))}
    </List>
  );

  if (!collapsible) {
    return (
      <Box component="section" aria-label={title || sectionId} sx={{ px: 0.5, py: 0 }}>
        {list}
      </Box>
    );
  }

  return (
    <Box component="section" sx={{ px: 0.5, py: 0.5 }}>
      <ButtonBase
        component="button"
        type="button"
        onClick={onToggle}
        focusRipple
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          py: 1.125,
          px: 1.75,
          mb: expanded ? 1 : 0,
          borderRadius: '10px',
          textAlign: 'left',
          color: sectionTitleColor,
          transition: (t) =>
            t.transitions.create(['background-color'], {
              duration: 200,
              easing: t.transitions.easing.easeInOut,
            }),
          '&:hover': {
            bgcolor: sectionHoverBg,
          },
        }}
        aria-expanded={expanded}
        aria-controls={panelId}
        id={headingId}
      >
        <Typography
          component="h2"
          variant="subtitle2"
          sx={{
            fontSize: '0.8125rem',
            fontWeight: 600,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            color: 'inherit',
          }}
        >
          {title}
        </Typography>
        <ExpandMoreIcon
          aria-hidden
          sx={{
            fontSize: 20,
            flexShrink: 0,
            ml: 0.75,
            color: 'inherit',
            opacity: 0.85,
            transition: (t) =>
              t.transitions.create('transform', {
                duration: 220,
                easing: t.transitions.easing.easeInOut,
              }),
            transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)',
          }}
        />
      </ButtonBase>

      <Collapse in={expanded} timeout="auto" unmountOnExit={false}>
        <Box id={panelId} role="region" aria-labelledby={headingId}>
          {list}
        </Box>
      </Collapse>
    </Box>
  );
};

export default NavSection;
